import { useHistory, useParams } from 'react-router-dom'
import { buttonVariantOptions } from '../stories/atoms/button/constants'
import ModalsComponent from '../stories/atoms/modals'
import { modalSizeOptions } from '../stories/atoms/modals/constants'
import {
  ExportPlanModalContentContainer,
  ExportPlanDescription,
  IncludePinsSection,
  IncludePinsLabelContainer,
  IncludePinsLabel,
  IncludePinsSecondaryLabel,
  PinSizeSection,
  PinSizeLabel
} from './styled-components'
import { useIntl, FormattedMessage } from 'react-intl'
import { usePlanMenuListViewControl } from '../../hooks/use-plan-menu-list-view-control'
import PropTypes from 'prop-types'
import SwitchComponent from '../stories/atoms/switch'
import { switchSizeOptions } from '../stories/atoms/switch/constants'
import SegmentedPickerComponent from '../stories/atoms/segmented-pickers'
import { pinSizeOptions } from './constants'

const ExportPlanModal = ({
  title = null,
  onExport = () => {},
  onClose,
  isLoading,

  hasFilterApplied = false,
  totalPins = 0,
  selectedPinsCount = null,
  totalSelectedPlans = 1,

  isIncludingPins = false,
  handleIsIncludingPins = () => {},

  pinSize = pinSizeOptions.MEDIUM,
  handleChangePinSize = () => {}
}) => {
  const history = useHistory()
  const params = useParams()
  const { formatMessage } = useIntl()

  const { projectId } = params
  const { viewType } = usePlanMenuListViewControl()

  // Callers that render this modal on its own route (the plan-menu export
  // flow) leave `onClose` unset, so closing means navigating back to the
  // plan list. Callers that render it inline on top of an already-mounted
  // page (the plan viewer) pass `onClose` to just dismiss it in place.
  const handleClose =
    onClose ??
    (() => {
      history.push(`/project/${projectId}?view=${viewType}`)
    })

  const rightButtons = [
    {
      title: formatMessage({ id: 'cancel' }),
      variant: buttonVariantOptions['TERTIARY-HOLLOW'],
      action: handleClose,
      disabled: isLoading
    },
    {
      title: formatMessage({ id: 'export' }),
      variant: buttonVariantOptions.PRIMARY,
      action: onExport,
      isLoading,
      disabled: isLoading
    }
  ]

  const defineTitle = title ?? formatMessage({ id: 'exportPlan_title' })

  // Bulk export only supports taking every pin or none - a filtered/partial
  // pin selection only makes sense while exporting a single plan.
  const showFilteredPinsLabel = hasFilterApplied && totalSelectedPlans === 1

  const pinsLabelId = showFilteredPinsLabel
    ? 'plans_dialog_export_pins-included-filtered'
    : 'plans_dialog_export_pins-included'

  const pinsLabelValues = showFilteredPinsLabel
    ? { count: selectedPinsCount ?? totalPins, total: totalPins }
    : { count: totalPins }

  return (
    <ModalsComponent
      title={defineTitle}
      size={modalSizeOptions['X-SMALL']}
      rightButtons={rightButtons}
      isOpen
      showCloseButton={false}
    >
      <ExportPlanModalContentContainer>
        <ExportPlanDescription>
          <FormattedMessage
            id="plans_dialog_export_description"
            values={{ count: totalSelectedPlans }}
          />
        </ExportPlanDescription>

        <IncludePinsSection>
          <SwitchComponent
            size={switchSizeOptions.SMALL}
            defaultChecked={false}
            checked={isIncludingPins}
            onCheckedChange={handleIsIncludingPins}
          />

          <IncludePinsLabelContainer>
            <IncludePinsLabel>
              <FormattedMessage id="exportPlan_includePins" />
            </IncludePinsLabel>
            <IncludePinsSecondaryLabel>
              <FormattedMessage id={pinsLabelId} values={pinsLabelValues} />
            </IncludePinsSecondaryLabel>
          </IncludePinsLabelContainer>
        </IncludePinsSection>

        {isIncludingPins && (
          <PinSizeSection>
            <PinSizeLabel>
              <FormattedMessage id="exportPlan_pinSize" />
            </PinSizeLabel>

            <SegmentedPickerComponent.Root
              defaultValue={pinSizeOptions.MEDIUM}
              onValueChange={handleChangePinSize}
              value={pinSize}
              style={{ width: '100%' }}
            >
              <SegmentedPickerComponent.List>
                <SegmentedPickerComponent.Trigger value={pinSizeOptions.SMALL}>
                  <FormattedMessage id="photoSizeSelection_small" />
                </SegmentedPickerComponent.Trigger>
                <SegmentedPickerComponent.Trigger value={pinSizeOptions.MEDIUM}>
                  <FormattedMessage id="photoSizeSelection_medium" />
                </SegmentedPickerComponent.Trigger>
                <SegmentedPickerComponent.Trigger value={pinSizeOptions.LARGE}>
                  <FormattedMessage id="photoSizeSelection_large" />
                </SegmentedPickerComponent.Trigger>
              </SegmentedPickerComponent.List>
            </SegmentedPickerComponent.Root>
          </PinSizeSection>
        )}
      </ExportPlanModalContentContainer>
    </ModalsComponent>
  )
}

ExportPlanModal.propTypes = {
  onExport: PropTypes.func,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  hasFilterApplied: PropTypes.bool,
  totalPins: PropTypes.number,
  selectedPinsCount: PropTypes.number,
  isIncludingPins: PropTypes.bool,
  handleIsIncludingPins: PropTypes.func,
  totalSelectedPlans: PropTypes.number
}

export default ExportPlanModal
